import React from "react";
import Icon from "../../components/Icon";
import { Avatar, Button, Empty, SkeletonRows } from "../../components/primitives";
import ConfirmModal from "../../components/manage/ConfirmModal";
import { ERR_TEXT } from "./manageUtils";
import { ROLE_CLASS, ROLE_LABEL } from "../../lib/roles";
import useManageMembers, { idOf } from "./useManageMembers";

/** Members tab (logic in useManageMembers). */
export default function MembersTab({ club, onTransferred }) {
  const {
    user, menu, viewerIsAdmin, state, members, search, setSearch, confirm, setConfirm,
    isBusy, shown, leaders, plain, run, manageMenu, retry,
  } = useManageMembers({ club, onTransferred });

  const row = (m) => {
    const id = idOf(m);
    const self = id === user?.uid;
    const canRole = viewerIsAdmin && m.role !== "admin" && !self;
    const canRemove = !self && (viewerIsAdmin || m.role === "member");
    return (
      <tr key={m.id}>
        <td>
          <div className="row">
            <Avatar name={m.user?.full_name} url={m.user?.avatar_url} id={id} size={34} />
            <div style={{ minWidth: 0 }}>
              <b>{m.user?.full_name || "Unknown User"}{self ? " (you)" : ""}</b>
              <div className="meta ellip">{m.user?.email}</div>
            </div>
          </div>
        </td>
        <td className="muted">{[m.user?.major, m.user?.graduation_year].filter(Boolean).join(" · ")}</td>
        <td><span className={`role ${ROLE_CLASS[m.role] || "r-member"}`}>{ROLE_LABEL[m.role] || "Member"}</span></td>
        <td className="mg-row-actions">
          {canRole && <Button variant="sec" size="xs" disabled={isBusy(id)} onClick={(e) => manageMenu(e, m)} data-testid={`manage.members.row.${id}.role`}>Manage role</Button>}{" "}
          {canRemove && <Button variant="danger" size="xs" disabled={isBusy(id)} onClick={() => setConfirm({ kind: "remove", member: m })} data-testid={`manage.members.row.${id}.remove`}>Remove</Button>}
        </td>
      </tr>
    );
  };

  const table = (title, list) => list.length > 0 && (
    <div style={{ marginBottom: 16 }}>
      <div className="label" style={{ margin: "0 6px 8px" }}>{title} ({list.length})</div>
      <div className="card tblwrap">
        <table className="tbl">
          <thead><tr><th>Member</th><th>Major</th><th>Role</th><th /></tr></thead>
          <tbody>{list.map(row)}</tbody>
        </table>
      </div>
    </div>
  );

  if (state.loading) return <SkeletonRows rows={5} />;
  if (state.error) {
    return (
      <Empty icon="alert" title="Couldn't load members">
        <div>{ERR_TEXT}</div>
        <Button size="sm" style={{ marginTop: 10 }} onClick={retry} data-testid="manage.members.retry">Retry</Button>
      </Empty>
    );
  }

  const name = confirm?.member?.user?.full_name || "this member";
  const copy = {
    promote: ["Promote to E-Board", "Promote", false, `Make ${name} an E-Board member?`],
    demote: ["Demote to Member", "Demote", false, `Make ${name} a regular member?`],
    transfer: ["Make Admin Control", "Yes, Transfer", true, "Are you sure you want to make admin control to this member? You will become a regular member."],
    remove: ["Remove Member", "Remove", true, `Remove ${name} from ${club.name}?`],
  }[confirm?.kind];

  return (
    <>
      <div className="search" style={{ boxShadow: "var(--shCard)", marginBottom: 14 }}>
        <Icon name="search" size={18} />
        <input placeholder="Search members" aria-label="Search members" data-testid="manage.members.search" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {shown.length === 0 ? (
        <Empty icon="users" title={members.length === 0 ? "No members yet" : "No matches"}>
          {members.length === 0 ? "Approved applicants show up here." : "Try a different search."}
        </Empty>
      ) : (
        <>{table("Leadership", leaders)}{table("Members", plain)}</>
      )}
      {menu}
      {confirm && (
        <ConfirmModal title={copy[0]} confirmLabel={copy[1]} danger={copy[2]} testId={`manage.members.${confirm.kind}Confirm`} onConfirm={run} onClose={() => setConfirm(null)}>
          {copy[3]}
        </ConfirmModal>
      )}
    </>
  );
}
